/**
 * AWS Shadow AI Discovery — Setup Instructions
 *
 * Walks the user through deploying the read-only CloudFormation stack and
 * copying the resulting role ARN. Shows the server-issued ExternalId (copyable)
 * and a button to download the CloudFormation template. Mirrors the structure
 * of BedrockSetupInstructions.
 *
 * Trust framing is deliberate: the role is READ-ONLY and the template never
 * requests secret values (no `secretsmanager:GetSecretValue`, no `Invoke*`).
 * That absence is a selling point — we say so here.
 */

import { useState } from "react";
import { Copy, Check, Loader2, Download, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { AwsDiscoverySetupInstructions as SetupInstructionsType } from "@/types/awsDiscovery";

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      aria-label={`Copy ${label}`}
      className="h-7 px-2"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

interface AwsDiscoverySetupInstructionsProps {
  setupInstructions: SetupInstructionsType | undefined;
  isLoading: boolean;
}

export function AwsDiscoverySetupInstructions({
  setupInstructions,
  isLoading,
}: AwsDiscoverySetupInstructionsProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Generating your unique external ID and CloudFormation template…
      </div>
    );
  }

  if (!setupInstructions) return null;

  const {
    external_id,
    cloudformation_template,
    our_account_id,
    suggested_regions,
    instructions,
    console_quick_create_url,
  } = setupInstructions;

  // `instructions` is a single numbered-lines STRING from the backend, not an array.
  const instructionLines = instructions
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  const handleDownloadTemplate = () => {
    const blob = new Blob([cloudformation_template], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "vortal-shadow-ai-discovery.yaml";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5 pt-3">
      {/* Trust banner */}
      <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
        <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium">Read-only, and we never read your secrets.</p>
          <p className="text-xs mt-1">
            This role only lists and describes your AI infrastructure. It can't read
            secret values, can't invoke models, and can't change anything. The template
            grants no <code className="font-mono">GetSecretValue</code> and no{" "}
            <code className="font-mono">Invoke*</code> permissions. Inspect the YAML
            before you deploy it.
          </p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Deploy the CloudFormation stack below in the AWS account you want to scan. It
        creates a scoped read-only IAM role Vortal assumes from account{" "}
        <code className="bg-muted px-1 py-0.5 rounded text-xs">{our_account_id}</code>{" "}
        using the External ID shown here.
      </p>

      {/* Step 1 — download + deploy */}
      <div className="space-y-3">
        <p className="text-sm font-medium">1. Download the CloudFormation template</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleDownloadTemplate}>
            <Download className="h-4 w-4 mr-2" />
            Download template (YAML)
          </Button>
          <CopyButton value={cloudformation_template} label="CloudFormation template" />
          <span className="text-xs text-muted-foreground">Or copy it to inspect first.</span>
        </div>

        <details className="rounded-md border bg-background">
          <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-muted-foreground">
            View template
          </summary>
          <pre className="px-3 pb-3 text-xs font-mono overflow-x-auto max-h-60">
            {cloudformation_template}
          </pre>
        </details>

        {console_quick_create_url && (
          <p className="text-sm">
            Or{" "}
            <a
              href={console_quick_create_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary underline"
            >
              one-click deploy in the AWS console
            </a>
            .
          </p>
        )}

        <p className="text-sm font-medium">
          2. Deploy it in <strong>AWS Console → CloudFormation → Create stack</strong>{" "}
          (upload the template file). The External ID below is already baked into the
          stack's trust policy.
        </p>
      </div>

      {/* External ID */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            External ID
          </Label>
          <CopyButton value={external_id} label="external ID" />
        </div>
        <div className="rounded-md bg-background border px-3 py-2 font-mono text-sm select-all">
          {external_id}
        </div>
      </div>

      {/* Steps from backend. `instructions` is a numbered-lines string; render each
          line as preformatted text (the lines already carry their own numbers). */}
      {instructionLines.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Steps
          </Label>
          <div className="space-y-1 text-sm text-muted-foreground">
            {instructionLines.map((line, idx) => (
              <p key={idx} className="whitespace-pre-wrap">
                {line}
              </p>
            ))}
          </div>
        </div>
      )}

      {suggested_regions.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Suggested regions to scan:{" "}
          <span className="font-mono">{suggested_regions.join(", ")}</span>
        </p>
      )}

      {/* Final step — role ARN */}
      <div className="space-y-1">
        <p className="text-sm font-medium">
          3. When the stack finishes, open its <strong>Outputs</strong> tab, copy the{" "}
          <strong>RoleArn</strong>, and paste it into the form below.
        </p>
        <p className="text-xs text-muted-foreground">
          The ARN looks like:{" "}
          <code className="bg-muted px-1 py-0.5 rounded">
            arn:aws:iam::123456789012:role/VortalShadowAiDiscovery
          </code>
        </p>
      </div>
    </div>
  );
}
